import { useState, useEffect, useCallback } from "react";
import { Users, Plus, Edit2, Trash2, Save, X, Key, Database, LogOut, RefreshCw } from "lucide-react";
import { USERS_URL, H, fetchAllUsers } from "./Login";

const EMPTY_FORM = { username: "", password: "", baseid: "", role: "user" };

export default function AdminPanel({ admin, onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // null = closed, "create" = new user form, user._id = edit form
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const all = await fetchAllUsers();
      setUsers(all);
    } catch {
      setError("Failed to load users.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  function openCreate() {
    setForm(EMPTY_FORM);
    setModal("create");
    setError("");
    setSuccess("");
  }

  function openEdit(user) {
    setForm({ username: user.username || "", password: user.password || "", baseid: user.baseid || "", role: user.role || "user" });
    setModal(user._id);
    setError("");
    setSuccess("");
  }

  function closeModal() { setModal(null); setForm(EMPTY_FORM); setError(""); }

  function flash(msg) {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.username.trim() || !form.password.trim()) {
      setError("Username and password are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const body = {
        fields: {
          username: form.username.trim(),
          password: form.password,
          baseid: form.baseid.trim(),
          role: form.role,
        }
      };
      const r = await fetch(USERS_URL, {
        method: "POST",
        headers: { ...H, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.error) throw new Error(d.error.message || "Airtable error");
      closeModal();
      await loadUsers();
      flash("User created successfully.");
    } catch (err) {
      setError(err.message || "Failed to create user.");
    }
    setSaving(false);
  }

  async function handleUpdate(e) {
    e.preventDefault();
    if (!form.username.trim() || !form.password.trim()) {
      setError("Username and password are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const body = {
        fields: {
          username: form.username.trim(),
          password: form.password,
          baseid: form.baseid.trim(),
          role: form.role,
        }
      };
      const r = await fetch(`${USERS_URL}/${modal}`, {
        method: "PATCH",
        headers: { ...H, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.error) throw new Error(d.error.message || "Airtable error");
      closeModal();
      await loadUsers();
      flash("User updated successfully.");
    } catch (err) {
      setError(err.message || "Failed to update user.");
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    setSaving(true);
    setError("");
    try {
      const r = await fetch(`${USERS_URL}/${id}`, { method: "DELETE", headers: H });
      const d = await r.json();
      if (d.error) throw new Error(d.error.message || "Airtable error");
      setDeleteConfirm(null);
      await loadUsers();
      flash("User deleted.");
    } catch (err) {
      setError(err.message || "Failed to delete user.");
    }
    setSaving(false);
  }

  const isEditing = modal && modal !== "create";

  return (
    <div style={{ minHeight: "100vh", background: "#F8F9FB", fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet"/>

      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #E5E7EB", padding: "18px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(135deg, #3B82F6, #8B5CF6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>L</span>
          </div>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 750, margin: 0, letterSpacing: "-0.03em" }}>Admin Panel</h1>
            <p style={{ fontSize: 11.5, color: "#9CA3AF", margin: 0 }}>Logged in as <strong>{admin.username}</strong></p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={loadUsers} disabled={loading} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 13px", fontSize: 11.5, fontWeight: 600, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 9, color: "#6B7280", cursor: "pointer" }}>
            <RefreshCw size={12} style={loading ? { animation: "spin 1s linear infinite" } : {}}/> Refresh
          </button>
          <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 13px", fontSize: 11.5, fontWeight: 600, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 9, color: "#EF4444", cursor: "pointer" }}>
            <LogOut size={12}/> Logout
          </button>
        </div>
      </div>

      <div style={{ padding: "28px", maxWidth: 900, margin: "0 auto" }}>
        {/* Flash messages */}
        {success && (
          <div style={{ background: "#DCFCE7", border: "1px solid #86EFAC", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#16A34A", marginBottom: 16 }}>
            {success}
          </div>
        )}
        {error && !modal && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#DC2626", marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Users table */}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, overflow: "hidden" }}>
          <div style={{ padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #F3F4F6" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Users size={16} color="#3B82F6"/>
              <span style={{ fontSize: 14, fontWeight: 650, color: "#111827" }}>Users ({users.length})</span>
            </div>
            <button
              onClick={openCreate}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, background: "linear-gradient(135deg, #3B82F6, #8B5CF6)", color: "#fff", border: "none", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}
            >
              <Plus size={13}/> New User
            </button>
          </div>

          {loading ? (
            <div style={{ padding: "40px", textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Loading users…</div>
          ) : users.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>No users yet. Create one to get started.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#F9FAFB" }}>
                    {["Username", "Role", "Base ID", "Password", "Actions"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "11px 16px", fontWeight: 600, color: "#6B7280", borderBottom: "1px solid #E5E7EB", fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u._id} style={{ borderBottom: "1px solid #F3F4F6" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={{ padding: "12px 16px", fontWeight: 600, color: "#111827" }}>{u.username}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{
                          padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: u.role === "admin" ? "#EDE9FE" : "#DBEAFE",
                          color: u.role === "admin" ? "#7C3AED" : "#2563EB"
                        }}>
                          {u.role || "user"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px", color: "#6B7280", fontFamily: "monospace", fontSize: 12 }}>
                        {u.baseid ? (
                          <span title={u.baseid}>{u.baseid.substring(0, 14)}…</span>
                        ) : <span style={{ color: "#D1D5DB" }}>not set</span>}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontFamily: "monospace", color: "#9CA3AF", letterSpacing: "0.15em" }}>{"•".repeat(Math.min(u.password?.length || 6, 8))}</span>
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => openEdit(u)}
                            style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE", borderRadius: 7, cursor: "pointer" }}
                          >
                            <Edit2 size={11}/> Edit
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(u._id)}
                            style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA", borderRadius: 7, cursor: "pointer" }}
                          >
                            <Trash2 size={11}/> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit Modal */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "32px", width: "100%", maxWidth: 440, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#111827" }}>
                {isEditing ? "Edit User" : "Create New User"}
              </h2>
              <button onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }}>
                <X size={18}/>
              </button>
            </div>

            <form onSubmit={isEditing ? handleUpdate : handleCreate} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <FormField label="Username" icon={<Users size={13}/>}>
                <input
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  required
                  placeholder="e.g. john_doe"
                  style={inputStyle}
                />
              </FormField>

              <FormField label="Password" icon={<Key size={13}/>}>
                <input
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                  placeholder="Plain text password"
                  style={inputStyle}
                />
              </FormField>

              <FormField label="Airtable Base ID" icon={<Database size={13}/>}>
                <input
                  value={form.baseid}
                  onChange={e => setForm(f => ({ ...f, baseid: e.target.value }))}
                  placeholder="e.g. appXXXXXXXXXXXXXX"
                  style={inputStyle}
                />
              </FormField>

              <FormField label="Role" icon={<Users size={13}/>}>
                <select
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  style={{ ...inputStyle, background: "#fff" }}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </FormField>

              {error && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", fontSize: 12.5, color: "#DC2626" }}>
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button type="button" onClick={closeModal} style={{ flex: 1, padding: "10px", fontSize: 13, fontWeight: 600, background: "#F3F4F6", color: "#374151", border: "none", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px", fontSize: 13, fontWeight: 700, background: saving ? "#93C5FD" : "linear-gradient(135deg, #3B82F6, #8B5CF6)", color: "#fff", border: "none", borderRadius: 9, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  <Save size={13}/> {saving ? "Saving…" : isEditing ? "Update User" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px", width: "100%", maxWidth: 360, boxShadow: "0 20px 60px rgba(0,0,0,0.15)", textAlign: "center" }}>
            <div style={{ width: 48, height: 48, background: "#FEF2F2", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Trash2 size={22} color="#EF4444"/>
            </div>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 8px", color: "#111827" }}>Delete User?</h3>
            <p style={{ fontSize: 13, color: "#6B7280", margin: "0 0 24px" }}>This action cannot be undone.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: "10px", fontSize: 13, fontWeight: 600, background: "#F3F4F6", color: "#374151", border: "none", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} disabled={saving} style={{ flex: 1, padding: "10px", fontSize: 13, fontWeight: 700, background: "#EF4444", color: "#fff", border: "none", borderRadius: 9, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {saving ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const inputStyle = {
  padding: "9px 12px", fontSize: 13.5, border: "1px solid #E5E7EB",
  borderRadius: 8, outline: "none", fontFamily: "inherit", width: "100%",
  boxSizing: "border-box",
};

function FormField({ label, icon, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", display: "flex", alignItems: "center", gap: 5 }}>
        {icon} {label}
      </label>
      {children}
    </div>
  );
}
